/* eslint-disable react/display-name */
import SanityBlockContent from "@sanity/block-content-to-react";
import ErrorBoundary from "components/ErrorBoundary";
import React, { ReactNode } from "react";
import sanityLocation from "src/utils/sanityLocation";
import InlineImage from "./InlineImage";
import { buildFileUrl, getFileAsset } from "@sanity/asset-utils/src";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { matchesAny } from "src/utils/localization/geolocation";
import { SanityAirNonAirFilterOrLocationFilter } from "types/graphql-types";

const contentImageWidth = 640;

const BlockRenderer = (props) => {
  if (props.node?.style === "disclaimer") {
    return <small>{props.children}</small>;
  }

  // Fall back to default handling
  return SanityBlockContent.defaultSerializers.block(props);
};

const InlineLocalizedPhoneNumber = () => {
  const geoLocation = useGeolocation();
  return <span>{geoLocation?.phone}</span>;
};

const LocationSpecificTextSection: React.FC<{
  mark: {
    _key: string;
    _type: string;
    locationFilters: SanityAirNonAirFilterOrLocationFilter[];
  };
}> = ({ mark, children }) => {
  const geolocation = useGeolocation();
  const countyCode = geolocation.countryCode;
  const isMatching = countyCode
    ? matchesAny(mark.locationFilters, countyCode)
    : false;

  return <span>{isMatching ? children : ""}</span>;
};

const serializers = (placeholderResolver) => ({
  marks: {
    placeholder: ({ children }: { children }) => placeholderResolver(children),
    localizedPhoneNumber: () => <InlineLocalizedPhoneNumber />,
    locationSpecificTextSection: ({ children, mark }) => (
      <LocationSpecificTextSection mark={mark}>
        {children}
      </LocationSpecificTextSection>
    ),
    sup: ({ children }: { children }) => <sup>{children}</sup>,
    sub: ({ children }: { children }) => <sub>{children}</sub>,
    link: ({ mark, children }) =>
      mark.openInNewTab ? (
        <a href={mark.href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ) : (
        <a href={mark.href}>{children}</a>
      ),
    linkToFile: ({ mark, children }) => {
      const fileAsset = getFileAsset(mark.file.asset, sanityLocation);
      const fileURL = buildFileUrl(fileAsset, sanityLocation);
      if (mark.forceDownload)
        return (
          <a href={`${fileURL}?dl=`} download>
            {children}
          </a>
        );
      else return <a href={fileURL}>{children}</a>;
    }
  },
  types: {
    inlineImage: ({ node }) => {
      if (node) return <InlineImage {...node} />;
      return null;
    },
    imageWithAlign: ({ node }) => {
      //deprecated
      if (node) return <InlineImage {...node} />;
      return null;
    }
  },
  block: BlockRenderer
});

export type PlaceholderResolver = (placeholder: string[]) => ReactNode;

export interface BlockContentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blocks?: string | any;
  className?: string;
  placeholderResolver?: PlaceholderResolver;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const defaultPlaceholderResolver = (k) => k;

const BlockContent: React.FC<BlockContentProps> = ({
  blocks,
  className = "",
  placeholderResolver = defaultPlaceholderResolver
}) => {
  if (!blocks) return null;
  if (typeof blocks == "string") return blocks;
  if (!Array.isArray(blocks)) return blocks;

  return (
    <ErrorBoundary>
      <SanityBlockContent
        className={className}
        blocks={blocks || []}
        serializers={serializers(placeholderResolver)}
        projectId={sanityLocation.projectId}
        dataset={sanityLocation.dataset}
        imageOptions={{ w: contentImageWidth }}
      />
    </ErrorBoundary>
  );
};

export default BlockContent;
